import { Face } from '@tensorflow-models/face-detection';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Tooltip from 'react-tooltip-lite';

import { IAddWorkerForm } from '@/modules/workforce/Crew/types';
import { Typography } from '@/shared/components/atoms';
import { ImagePickerNewD } from '@/shared/components/atoms/ImagePickerNewD';
import useImageRecognition from '@/shared/hooks/useImageRecognition';

import Check from '~/images/shared/CheckMarkWithoutBG.svg';
import Close from '~/images/shared/CloseRhombus.svg';
import ExleminationMark from '~/images/shared/ExleminationMark.svg';

const FacialRecognition = () => {
  const { t } = useTranslation('common');
  const [isBlurry, setIsBlurry] = useState<boolean>(false);
  const [facesList, setFacesList] = useState<Face[] | undefined>(undefined);
  // TODO: khalid - update type to be exported from namespace
  const {
    watch,
    setError,
    clearErrors,
    trigger,
    formState: { dirtyFields },
  } = useFormContext<IAddWorkerForm>();
  const { detectFaces, checkIfImageBlurry } = useImageRecognition();
  const profilePictureWatcher = watch('profilePicture');

  useEffect(() => {
    const checkImage = async () => {
      if (
        profilePictureWatcher &&
        typeof profilePictureWatcher !== 'string' &&
        'file' in profilePictureWatcher[0] &&
        profilePictureWatcher?.[0]?.file
      ) {
        setIsBlurry(await checkIfImageBlurry(profilePictureWatcher[0].file));
        setFacesList((await detectFaces(profilePictureWatcher[0].file)) ?? []);
      }
    };
    checkImage();
  }, [profilePictureWatcher, checkIfImageBlurry, detectFaces, dirtyFields]);

  useEffect(() => {
    if (profilePictureWatcher !== undefined) {
      if (isBlurry || (facesList !== undefined && facesList?.length !== 1)) {
        setError('profilePicture', {
          message: t('required-right-data'),
        });
      } else {
        clearErrors('profilePicture');
        trigger();
      }
    } else {
      setFacesList(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlurry, facesList, profilePictureWatcher]);

  return (
    <div className='mt-16 flex flex-col gap-16'>
      {(isBlurry || (facesList !== undefined && facesList.length !== 1)) &&
        profilePictureWatcher !== undefined && (
          <div className='flex w-full items-center gap-2 rounded-xl bg-error-90 p-16 text-error-20'>
            <span className='text-24'>
              <Close />
            </span>
            <span className='text-14'>
              {t('photo-not-matching-requirements')}
            </span>
          </div>
        )}
      {profilePictureWatcher !== undefined &&
        !isBlurry &&
        facesList !== undefined &&
        facesList.length === 1 && (
          <div className='flex w-full items-center gap-2 rounded-xl bg-green-100 p-16 text-green-300'>
            <span className='text-24'>
              <Check />
            </span>
            <span className='text-14'>{t('photo-verified')}</span>
          </div>
        )}
      <div className='flex flex-col gap-1'>
        <Typography
          variant='h2'
          weight='semiBold'
          className='flex items-center gap-2 capitalize'
        >
          {t('face-id')}{' '}
          <Tooltip
            content={
              <Typography className='w-fit min-w-[100px] max-w-[300px] rounded-lg bg-background-200 px-16 py-4 text-center'>
                {t('face-id-tooltip')}
              </Typography>
            }
            direction='down'
            arrow={false}
            tipContentHover
          >
            <div className='flex items-center justify-center gap-2'>
              <ExleminationMark />
            </div>
          </Tooltip>
        </Typography>
        <Typography
          weight='medium'
          variant='caption'
          className='text-surface-10'
        >
          {t('face-id-description')}
        </Typography>
      </div>
      <ImagePickerNewD fullWidth id='profilePicture' />
    </div>
  );
};

export default FacialRecognition;
